import { useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { AppBar, Avatar, Toolbar, Typography, Box, useMediaQuery, useTheme, Divider, Menu, MenuItem, styled, IconButton, Button, Popper, Paper, MenuList, Grow, ClickAwayListener } from '@mui/material';
import { useAuth } from "../hooks/useAuth";
import { Icon } from '../components/icons';
import ProjectSidebar from '../components/SideBar/ProjectSidebar';
import SwitchButton from '../components/Buttons/modeButton';
import { useColorScheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

const MenuItemStyled = styled(MenuItem)(({ theme }) => ({
  '&:hover .MuiBox-root, &:hover .MuiBox-root svg': {
    color: theme.palette.primary.main
  }
}));

const MenuStyles = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  color: 'text.primary',
  textDecoration: 'none',
  '& svg': {
    mr: 2.5,
    fontSize: '1.5rem',
    color: 'text.secondary'
  }
};

const languageIconMap = {
  ko: 'southKoreaIcon',
  en: 'usaIcon',
  jp: 'japanIcon'
};

const ProjectAppLayout = () => {
  const { t, i18n } = useTranslation();
  const user = useSelector((state) => state.auth.user);
  const theme = useTheme();
  const auth = useAuth();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

  // const [language, setLanguage] = useState(localStorage.getItem('insightwin-language') || 'ko');
    // 아바타 드롭다운
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorElLanguage, setAnchorElLanguage] = useState(null);

  const handleDropdownOpen = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleDropdownClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    auth.logout();
  };

  // MUI 컬러 스키마 훅 사용
  const { mode, setMode } = useColorScheme();

  // 테마 모드 전환 핸들러
  const handleThemeChange = () => {
    const newMode = mode === 'light' ? 'dark' : 'light';
    setMode(newMode);
    localStorage.setItem('insightwin-mui-mode', newMode);  // 로컬 스토리지에 저장
  };

  const handleLanguageChange = (newLang) => {
    i18n.changeLanguage(newLang);
    handleLanguageDropdownClose();
  };

  const handleLanguageDropdownOpen = event => {
    setAnchorElLanguage(event.currentTarget);
  };

  const handleLanguageDropdownClose = () => {
    setAnchorElLanguage(null);
  };

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      {/* 상단 전체를 차지하는 AppBar */}
      <AppBar
        color="default"
        elevation={2}
        position="sticky"
        sx={(theme) => ({
          borderBottom: `1px solid ${theme.palette.divider}`,
          borderRadius: 0,
          boxShadow: "none",
          alignItems: "center",
          color: "text.primary",
          justifyContent: "center",
          transition: "border-bottom 0.2s ease-in-out",
          backgroundColor: "rgba(255, 255, 255, 0.88)",
        })}
      >
        <Toolbar disableGutters sx={{ width: "100%", paddingInline: "30px" }}>
          <Box 
            sx={{ 
              display: "flex", 
              alignItems: "center", 
              justifyContent: "space-between", 
              width: "100%" 
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 500, 
                  lineHeight: "24px", 
                  opacity: 0.8,
                  marginRight: '40px'
                }}>
                {t('layouts.project.platformTitle')}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center" }}>
              {/* 상단 네비게이션 메뉴 */}

              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", gap: '6px', marginLeft: '30px' }}>
                <IconButton onClick={handleLanguageDropdownOpen}>
                  <Icon name={languageIconMap[i18n.language]} style={{ width: '26px', height: '26px' }} />
                </IconButton>
                <IconButton onClick={handleThemeChange}>
                  <Icon 
                    name={mode === 'light' ? "sunOutlineIcon" : "moonOutlineIcon"} 
                    style={{ 
                      width: '26px', 
                      height: '26px', 
                      '--stroke-width': '3px' 
                    }} 
                  />
                </IconButton>
                <IconButton>
                  <Icon name="bellOutlineIcon" style={{ width: '26px', height: '26px', '--stroke-width': '3px' }} />
                </IconButton>
                <IconButton onClick={handleDropdownOpen}>
                  <Icon name="userIcon" style={{ width: '26px', height: '26px', '--stroke-width': '3px' }} />
                </IconButton>
              </Box>
            </Box>
            <Menu
              anchorEl={anchorElLanguage}
              open={Boolean(anchorElLanguage)}
              onClose={handleLanguageDropdownClose}
              sx={{
                "& .MuiMenu-paper": {
                  mt: "12px",
                },
              }}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
              <MenuItemStyled selected={i18n.language === 'ko'} onClick={() => handleLanguageChange('ko')}>
                <Box sx={MenuStyles}>
                  <Icon name="southKoreaIcon" style={{ width: "22px", height: "22px" }} />
                  한국어
                </Box>
              </MenuItemStyled>
              <MenuItemStyled selected={i18n.language === 'jp'} onClick={() => handleLanguageChange('jp')}>
                <Box sx={MenuStyles}>
                  <Icon name="japanIcon" style={{ width: "22px", height: "22px" }} />
                  日本語
                </Box>
              </MenuItemStyled>
              <MenuItemStyled selected={i18n.language === 'en'} onClick={() => handleLanguageChange('en')}>
                <Box sx={MenuStyles}>
                  <Icon name="usaIcon" style={{ width: "22px", height: "22px" }} />
                  English
                </Box>
              </MenuItemStyled>
            </Menu>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleDropdownClose}
              sx={{
                "& .MuiMenu-paper": {
                  width: 210,
                  mt: "12px",
                },
              }}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <Box sx={{ display: "flex", alignItems: "center", px: 5, py: 1 }}>
                <Avatar sx={{ width: 38, height: 38, mr: 4 }}>
                  <Icon name="userIcon" />
                </Avatar>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography variant="h6">{user.user_name}</Typography>
                  <Typography variant="body2" sx={{ color: "text.disabled" }}>
                    {user.group_name}
                  </Typography>
                </Box>
              </Box>
              <Divider sx={{ my: 1 }} />
              <MenuItemStyled onClick={handleDropdownClose}>
                <Box sx={MenuStyles}>
                  <Icon name="userEditIcon" style={{ width: "22px", height: "22px" }} />
                  {t('layouts.profile.settings')}
                </Box>
              </MenuItemStyled>
              <MenuItemStyled onClick={handleLogout}>
                <Box sx={MenuStyles}>
                  <Icon name="logoutIcon" style={{ width: "22px", height: "22px" }} />
                  {t('layouts.profile.logout')}
                </Box>
              </MenuItemStyled>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* 사이드바 + 메인 컨텐츠 영역 */}
      <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
        {/* 좌측 사이드바 */}
        <ProjectSidebar />

        {/* 컨텐츠 영역 */}
        <Box sx={{ flexGrow: 1, overflow: "hidden" }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default ProjectAppLayout;